// FinanceHeroCard — 금융탭 "투자 자아 카드" HERO + 가챠 코스메틱 이펙트 연동
//
// 캐릭터(무료 IP)는 항상 표시 + finance_hero 슬롯에 장착한 코스메틱 카드의 등급으로 "꾸미기":
//   L(전설) = 회전 프리즘 테두리 + 홀로/프리즘 풀셋 + L카드 전용 일러
//   E(에픽) = 은은한 홀로(sheen/glare)
//   N·R·미장착 = 이펙트 없음(현 잉크그린 단색) + 시그니처 일러
// 이펙트 CSS는 CosmeticCard(holo-prism)와 동일 계열 — 스펙트럼 색은 cs-tokens 예외(무지개 본질).
// 일러 배치는 financeHeroTune의 inset 모델(상/우/하 여백 px) — 코칭 줄 수가 변해도 여백 일정.
// @ts-nocheck
import { useQuery } from '@tanstack/react-query';
import { apiFetch, USE_MOCK } from '../../lib/api';
import { parseCardKey } from '@core/sim/cosmetic/cosmeticCatalog';
import { cosmeticMock } from '../../lib/mock/cosmetic';
import { gradeToSource, heroCharSrc, heroInset } from './financeHeroTune';
// 🦌 록이 gaze(꽃왕관 응시) — finance_hero 슬롯 무료 기본값. L카드 미장착(sq)일 때만 모션,
//   장착 시엔 전용 L카드 일러 유지(가챠 보상 보존). design doc 2026-06-24-roki-motion-gacha-showcase §6.
import rokiGaze from './_heatmap/roki_gaze_alpha.mov';

const SH3 = '0 20px 40px rgba(26,23,20,.10), 0 4px 10px rgba(26,23,20,.06)';

// 이펙트 레이어 CSS (CosmeticCard 계열 · fhc 스코프). 1회 주입.
const FX_CSS = `
.fhc-pwrap{padding:3px;border-radius:21px;
  background:conic-gradient(from var(--pa,0deg), #f0c75e, #ef7d57, #6ea8f7, #b07df0, #6ee7c8, #f0c75e);
  animation:fhc-spin 11s linear infinite;}
/* 배경=accent인 카드에 같은 색 border는 무의미(§G-2 자기색 테두리 제거, 2026-07-05 66감사 #9). 회전 링(.fhc-pwrap)이 경계 담당. */
.fhc{position:relative;overflow:hidden;background:var(--cs-accent);
  border-radius:var(--cs-r-lg);padding:0;cursor:pointer;min-height:188px;}
.fhc-in{position:relative;z-index:12;padding:20px;padding-right:42%;}
.fhc-char{position:absolute;z-index:11;top:var(--top,20px);right:var(--right,20px);bottom:var(--bottom,20px);left:auto;pointer-events:none;}
.fhc-char img,.fhc-char video{height:100%;width:auto;object-fit:contain;object-position:bottom right;display:block;
  filter:drop-shadow(0 4px 10px rgba(0,0,0,.35)) drop-shadow(0 0 14px rgba(255,255,255,.5));}
.fhc .fx{position:absolute;inset:0;pointer-events:none;}
.fhc .fx-readmask{z-index:10;background:linear-gradient(100deg, rgba(20,34,27,.6) 0%, rgba(20,34,27,.15) 50%, transparent 70%);}
.fhc .sheen{mix-blend-mode:color-dodge;opacity:.2;z-index:4;
  background:repeating-linear-gradient(115deg, rgba(255,80,80,.6) 0%, rgba(255,200,60,.6) 6%, rgba(120,255,120,.6) 12%, rgba(80,200,255,.6) 18%, rgba(160,100,255,.6) 24%, rgba(255,80,80,.6) 30%);
  background-size:320% 320%;filter:saturate(1.25) brightness(.85);}
.fhc .glitter{mix-blend-mode:overlay;opacity:.42;z-index:5;
  background-image:radial-gradient(circle at 72% 26%, rgba(255,255,255,.95) .6px, transparent 1.5px),
    radial-gradient(circle at 88% 58%, rgba(255,255,255,.85) .5px, transparent 1.2px),
    radial-gradient(circle at 60% 80%, rgba(255,255,255,.95) .7px, transparent 1.6px);
  background-size:64px 64px;}
.fhc .glare{mix-blend-mode:soft-light;opacity:.62;z-index:6;
  background:radial-gradient(circle at 76% 36%, rgba(255,255,255,.92) 0%, transparent 48%);}
.fhc .prism{z-index:4;mix-blend-mode:color-dodge;opacity:.34;filter:saturate(1.35) brightness(.85) blur(1px);
  background:conic-gradient(from var(--pa,0deg) at 76% 40%,
    rgba(255,60,60,.7), rgba(255,220,60,.7), rgba(80,255,140,.7), rgba(60,200,255,.7), rgba(180,90,255,.7), rgba(255,60,60,.7));}
.fhc .sweep{z-index:7;mix-blend-mode:screen;opacity:0;
  background:linear-gradient(115deg, transparent 38%, rgba(255,255,255,.6) 49%, rgba(190,230,255,.7) 50%, rgba(255,255,255,.6) 51%, transparent 62%);
  background-size:260% 260%;animation:fhc-sweep 5.2s ease-in-out infinite;}
@keyframes fhc-sweep{0%,64%{opacity:0;background-position:120% 120%;}72%{opacity:.85;}88%,100%{opacity:0;background-position:-30% -30%;}}
@keyframes fhc-spin{to{--pa:360deg;}}
@keyframes fhc-flow{0%{background-position:0% 50%;}50%{background-position:300% 50%;}100%{background-position:0% 50%;}}
.fhc.idle .sheen{animation:fhc-flow 9s linear infinite;}
.fhc.lv-e .prism,.fhc.lv-e .sweep{display:none;}
.fhc.lv-e .sheen{opacity:.14;}
.fhc.lv-none .fx{display:none!important;}
`;
let fxInjected = false;
function injectFx() {
  if (fxInjected || typeof document === 'undefined') return;
  const el = document.createElement('style');
  el.dataset.financeHeroFx = '1';
  el.textContent = FX_CSS;
  document.head.appendChild(el);
  fxInjected = true;
}

/**
 * @param mbti     MBTI 페이지 데이터 (result·coaching)
 * @param charId   MBTI 매칭 캐릭터 slug (장착 카드 없을 때 기본)
 * @param mbtiCode MBTI 4글자 코드
 * @param onClick  카드 클릭 (보통 /mbti 이동)
 */
// insetOverride·charScale — 검수실(MotionLabScreen) 전용 슬라이더 주입용. prod에선 미전달 → 기본값.
export function FinanceHeroCard({ mbti, charId, mbtiCode, onClick, insetOverride, charScale = 1, offsetX = 0, offsetY = 0 }) {
  injectFx();

  // finance_hero 슬롯 장착 카드 조회 — MyScreen/CardCollection과 동일 queryKey라 캐시 공유
  const { data: cos } = useQuery({
    queryKey: ['mobile', 'cosmetic'],
    queryFn: () => (USE_MOCK ? Promise.resolve(cosmeticMock) : apiFetch('/api/mobile/cosmetic')),
    staleTime: 30_000,
  });

  // 장착 해석: 카드 → 등급·캐릭터 / base_cast(무료 캐릭터) / 미장착 → MBTI 매칭
  const slot = (cos?.equips || []).find((e) => e.slot_key === 'finance_hero');
  let grade = null;
  let slug = charId;
  if (slot?.card_id) {
    const card = (cos?.cards || []).find((c) => c.id === slot.card_id);
    const p = card ? parseCardKey(card.card_key) : null;
    if (p) { grade = p.grade; slug = p.slug; }
  } else if (slot?.base_cast) {
    slug = slot.base_cast;
  }

  const source = gradeToSource(grade);
  const inset = insetOverride || heroInset(source, slug);
  const fxClass = grade === 'L' ? 'lv-l' : grade === 'E' ? 'lv-e' : 'lv-none';
  const r = mbti?.result || {};

  const inner = (
    <div
      className={`fhc idle ${fxClass}`}
      onClick={onClick}
      style={{ boxShadow: SH3 }}
    >
      {/* 이펙트 레이어 (등급별 — lv-none이면 전부 숨김) */}
      <div className="fx fx-readmask" />
      <div className="fx sheen" />
      <div className="fx glitter" />
      <div className="fx prism" />
      <div className="fx glare" />
      <div className="fx sweep" />
      {/* 우측 큰 캐릭터 일러 (시그니처 / L카드) — 이펙트 위 흰 후광 */}
      <div className="fhc-char" style={{ '--top': `${inset.top}px`, '--right': `${inset.right}px`, '--bottom': `${inset.bottom}px`, transform: (charScale !== 1 || offsetX || offsetY)
          // 검수실(MotionLabScreen) 슬라이더가 주입되면 그쪽이 우선
          ? `translate(${offsetX}px, ${offsetY}px) scale(${charScale})`
          // prod: financeHeroTune의 dy/scale을 적용한다. 종전엔 록이만 이 자리에 하드코딩돼 있어
          //       다른 캐릭터는 세로 위치를 잡을 방법이 아예 없었다(2026-08-19 데이터로 이관).
          : (inset.dy || inset.scale)
            ? `translate(${slug === 'roki' && source === 'sq' ? 58 : 0}px, ${inset.dy || 0}px) scale(${inset.scale || 1})`
            : undefined, transformOrigin: 'bottom right' }}>
        {/* 🦌 록이 + 시그니처(sq) = gaze 모션 / 그 외(다른 캐릭터·L카드 장착) = 정지 일러(가챠 보상 보존) */}
        {slug === 'roki' && source === 'sq'
          ? <video src={rokiGaze} autoPlay loop muted playsInline aria-hidden />
          : <img src={heroCharSrc(source, slug)} alt="" />}
      </div>

      <div className="fhc-in">
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 11, fontWeight: 800, letterSpacing: '0.1em', color: 'var(--cs-gold-on-ink)' }}>
            <span style={{ fontSize: 14 }}>{r.emoji}</span>{mbtiCode}
          </span>
          {typeof r.rankPct === 'number' && (
            <span style={{ display: 'inline-flex', alignItems: 'center', height: 22, padding: '0 10px', borderRadius: 999, fontWeight: 700, fontSize: 11, color: 'var(--cs-accent)', background: 'var(--cs-gold-on-ink)' }}>자산 상위 {r.rankPct}%</span>
          )}
        </div>
        <div style={{ fontFamily: 'var(--cs-font-serif)', fontSize: 27, fontWeight: 800, color: 'var(--cs-accent-ink)', lineHeight: 1.25, letterSpacing: '-0.01em' }}>{r.nickname}</div>
        {r.description && <div style={{ fontSize: 12, fontWeight: 500, color: 'var(--cs-on-ink-dim)', lineHeight: 1.5, marginTop: 6 }}>{r.description}</div>}
        {/* 코칭칩 — Design System Badge(solid gold: 불투명 골드 + 대비 텍스트) 정합. 같은 카드의
            "자산 상위 N%" 배지(:130)와 동일 계열로 통일 — 좌테두리·아이콘 제거(AI풍 탈피, 효신님 2026-07-02).
            불투명 배경이라 캐릭터 겹침 해소(Day1 목적)도 유지 */}
        {mbti?.coaching?.message && (
          <div style={{ marginTop: 14, padding: '10px 13px', borderRadius: 'var(--cs-r-sm)', background: 'var(--cs-gold-on-ink)' }}>
            {/* "코칭"이라는 말도 자문 뉘앙스라 "참고"로 바꿨다(2026-08-19).
                문장 자체도 coaching.ts에서 행동 지시를 빼고 "참고 배분과의 차이" 서술로 전환. */}
            <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--cs-accent)', lineHeight: 1.45 }}>{mbti.coaching.month} 참고 · {mbti.coaching.message}</span>
            {/* 면책 — PRODUCT.md "면책 라벨 강제" 원칙이 이 카드에만 빠져 있었다.
                법적 설계(정보제공형 + 면책문구)의 후자에 해당하므로 문구와 함께 붙인다. */}
            <div style={{ marginTop: 5, fontSize: 10, fontWeight: 600, color: 'var(--cs-accent)', opacity: 0.72, lineHeight: 1.4 }}>
              정보 제공 목적 · 투자 권유 아님
            </div>
          </div>
        )}
      </div>
    </div>
  );

  // L = 회전 프리즘 테두리 래퍼, 그 외 = 단독
  return grade === 'L' ? <div className="fhc-pwrap" style={{ margin: '8px 0 28px' }}>{inner}</div> : <div style={{ margin: '8px 0 28px' }}>{inner}</div>;
}
